/**
 * Cache utilities for map data with direct cache manipulation
 * these functions when performing CRUD operations on sites/controllers
 * supports zero-cost cache updates without API calls!
 */

// Global references to cache operations
let cacheInvalidationFunction: (() => void) | null = null;
let directCacheOperations: {
  addSiteToCache?: (site: any) => void;
  removeSiteFromCache?: (siteId: string) => void;
  addControllerToCache?: (controller: any) => void;
  removeControllerFromCache?: (controllerId: string) => void;
  updateSiteInCache?: (site: any) => void;
  updateControllerInCache?: (controller: any) => void;
} = {};

/**
 * Register cache operations from MapDataContext
 * This is called automatically when MapDataProvider mounts
 */
export const registerCacheInvalidation = (invalidateFn: () => void) => {
  cacheInvalidationFunction = invalidateFn;
  console.log('CacheUtils: Cache invalidation registered');
};

/**
 * Register direct cache operations for zero-cost CRUD
 */
export const registerDirectCacheOperations = (operations: {
  addSiteToCache: (site: any) => void;
  removeSiteFromCache: (siteId: string) => void;
  addControllerToCache: (controller: any) => void;
  removeControllerFromCache: (controllerId: string) => void;
  updateSiteInCache: (site: any) => void;
  updateControllerInCache: (controller: any) => void;
}) => {
  directCacheOperations = operations;
  console.log('CacheUtils: Direct cache operations registered');
};

/**
 * Add a new site to cache without API call
 * Call this after successfully creating a site via API
 */
export const addSiteToCache = (site: any) => {
  if (directCacheOperations.addSiteToCache) {
    console.log('CacheUtils: Adding site to cache (zero API cost)', site.id);
    directCacheOperations.addSiteToCache(site);
  } else {
    console.warn('CacheUtils: Direct cache operations not available, falling back to full refresh');
    if (cacheInvalidationFunction) {
      cacheInvalidationFunction();
    }
  }
};

/**
 * Remove a site from cache without API call
 * Call this after successfully deleting a site via API
 */
export const removeSiteFromCache = (siteId: string) => {
  if (directCacheOperations.removeSiteFromCache) {
    console.log('CacheUtils: Removing site from cache (zero API cost)', siteId);
    directCacheOperations.removeSiteFromCache(siteId);
  } else {
    console.warn('CacheUtils: Direct cache operations not available, falling back to full refresh');
    if (cacheInvalidationFunction) {
      cacheInvalidationFunction();
    }
  }
};

/**
 * Add a new controller to cache without API call
 * Call this after successfully creating a controller via API
 */
export const addControllerToCache = (controller: any) => {
  if (directCacheOperations.addControllerToCache) {
    console.log('CacheUtils: Adding controller to cache (zero API cost)', controller.id);
    directCacheOperations.addControllerToCache(controller);
  } else {
    console.warn('CacheUtils: Direct cache operations not available, falling back to full refresh');
    if (cacheInvalidationFunction) {
      cacheInvalidationFunction();
    }
  }
};

/**
 * Remove a controller from cache without API call
 * Call this after successfully deleting a controller via API
 */
export const removeControllerFromCache = (controllerId: string) => {
  if (directCacheOperations.removeControllerFromCache) {
    console.log('CacheUtils: Removing controller from cache (zero API cost)', controllerId);
    directCacheOperations.removeControllerFromCache(controllerId);
  } else {
    console.warn('CacheUtils: Direct cache operations not available, falling back to full refresh');
    if (cacheInvalidationFunction) {
      cacheInvalidationFunction();
    }
  }
};

/**
 * Update a site in cache without API call
 * Call this after successfully updating a site via API
 */
export const updateSiteInCache = (site: any) => {
  if (directCacheOperations.updateSiteInCache) {
    console.log('CacheUtils: Updating site in cache (zero API cost)', site.id);
    directCacheOperations.updateSiteInCache(site);
  } else {
    console.warn('CacheUtils: Direct cache operations not available, falling back to full refresh');
    if (cacheInvalidationFunction) {
      cacheInvalidationFunction();
    }
  }
};

/**
 * Update a controller in cache without API call
 * Call this after successfully updating a controller via API
 */
export const updateControllerInCache = (controller: any) => {
  if (directCacheOperations.updateControllerInCache) {
    console.log('CacheUtils: Updating controller in cache (zero API cost)', controller.id);
    directCacheOperations.updateControllerInCache(controller);
  } else {
    console.warn('CacheUtils: Direct cache operations not available, falling back to full refresh');
    if (cacheInvalidationFunction) {
      cacheInvalidationFunction();
    }
  }
};

// Legacy functions for backward compatibility
export const invalidateCacheAfterSiteCreate = () => addSiteToCache;
export const invalidateCacheAfterSiteDelete = () => removeSiteFromCache;
export const invalidateCacheAfterControllerCreate = () => addControllerToCache;
export const invalidateCacheAfterControllerDelete = () => removeControllerFromCache;

/**
 * Generic function for full cache refresh
 * Only use when direct cache operations aren't sufficient
 */
export const invalidateCacheAfterStructuralChange = () => {
  if (cacheInvalidationFunction) {
    console.log('CacheUtils: Full cache refresh triggered');
    cacheInvalidationFunction();
  } else {
    console.warn('CacheUtils: Cache invalidation not available');
  }
};

/**
 * Enhanced hook for React components with zero-cost CRUD operations
 */
export const useCacheInvalidation = () => {
  return {
    // Zero-cost direct cache operations
    addSiteToCache,
    removeSiteFromCache,
    addControllerToCache,
    removeControllerFromCache,
    updateSiteInCache,
    updateControllerInCache,
    
    // Full refresh (use sparingly)
    invalidateAfterStructuralChange: invalidateCacheAfterStructuralChange,
  };
}; 